import {
  SEGMENT_FIELD_TYPES,
  SEGMENT_TYPES,
  URL_MAX_LENGTH,
  IMAGE_POSITION_OPTIONS,
  field,
  visualStyleProperty,
  globalColorProperties,
  commonSegment
} from "./segmentRegistryCore";

const commerceSegments = {
  [SEGMENT_TYPES.SPOTLIGHT]: commonSegment({
    id: SEGMENT_TYPES.SPOTLIGHT,
    visibilityPath: "sections.showSpotlight",
    requiresProducts: true,
    elementEditingReady: true,
    visibilityCompanionPaths: ["catalog.spotlight.enabled"],
    visibilityLabel: "Mostrar destacado",
    shellVisualBackground: false,
    shellVisualPalette: false,
    shellPropertyPaths: [
      "catalog.spotlight.primaryColor",
      "catalog.spotlight.secondaryColor",
    ],
    properties: [
      field("sections.showSpotlight", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Permitir mostrar el bloque",
      }),
      field("catalog.spotlight.enabled", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Activar bloque destacado",
      }),
      field("catalog.spotlight.eyebrow", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto superior",
        maxLength: 180,
      }),
      field("catalog.spotlight.title", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Título",
        maxLength: 220,
      }),
      field("catalog.spotlight.description", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Descripción",
        maxLength: 1200,
      }),
      field("catalog.spotlight.logoUrl", SEGMENT_FIELD_TYPES.IMAGE, {
        label: "Logo o imagen principal",
        maxLength: URL_MAX_LENGTH,
        altPath: "catalog.spotlight.logoAlt",
      }),
      field("catalog.spotlight.logoAlt", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto alternativo del logo",
        maxLength: 220,
      }),
      field("catalog.spotlight.backgroundImageUrl", SEGMENT_FIELD_TYPES.IMAGE, {
        label: "Imagen de fondo",
        maxLength: URL_MAX_LENGTH,
        positionPath: "catalog.spotlight.backgroundImagePosition",
      }),
      field("catalog.spotlight.backgroundImagePosition", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Posición de la imagen",
        options: IMAGE_POSITION_OPTIONS,
      }),
      field("catalog.spotlight.ctaLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Botón destacado",
        maxLength: 100,
      }),
      field("catalog.spotlight.ctaUrl", SEGMENT_FIELD_TYPES.URL, {
        label: "Destino del botón",
        maxLength: URL_MAX_LENGTH,
        destinationPicker: true,
      }),
      field("catalog.spotlight.ctaRequiresProducts", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "El botón requiere catálogo",
      }),
      field("catalog.spotlight.primaryColor", SEGMENT_FIELD_TYPES.COLOR, {
        label: "Color principal del destacado",
        maxLength: 100,
        colorScope: "section",
        fallbackPath: "theme.primaryColor",
      }),
      field("catalog.spotlight.secondaryColor", SEGMENT_FIELD_TYPES.COLOR, {
        label: "Color secundario del destacado",
        maxLength: 100,
        colorScope: "section",
        fallbackPath: "theme.secondaryColor",
      }),
      visualStyleProperty(SEGMENT_TYPES.SPOTLIGHT),
    ],
  }),
  [SEGMENT_TYPES.FEATURED]: commonSegment({
    id: SEGMENT_TYPES.FEATURED,
    visibilityPath: "sections.showFeatured",
    requiresProducts: true,
    elementEditingReady: true,
    elementTargets: {
      "catalog.featured.ctaLabel": {
        kind: "button",
        label: "Botón de catálogo",
        labelPath: "catalog.featured.ctaLabel",
        chips: ["Catálogo"],
      },
      "@featured.cards": {
        kind: "card-system",
        label: "Cards del catálogo",
        fieldPrefix: "catalog.cards.",
        chips: ["Global"],
      },
    },
    shellAdvancedGroups: [
      {
        label: "Mensajes del catálogo",
        paths: [
          "catalog.featured.loadingText",
          "catalog.featured.emptyText",
          "catalog.featured.errorText",
        ],
      },
    ],
    properties: [
      field("sections.showProducts", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar catálogo",
      }),
      field("sections.showFeatured", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar destacados",
      }),
      field("catalog.featured.eyebrow", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto superior",
        maxLength: 180,
      }),
      field("catalog.featured.title", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Título",
        maxLength: 220,
      }),
      field("catalog.featured.subtitle", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Descripción",
        maxLength: 900,
      }),
      field("catalog.featured.loadingText", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Mensaje de carga",
        maxLength: 220,
      }),
      field("catalog.featured.emptyText", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Mensaje sin resultados",
        maxLength: 300,
      }),
      field("catalog.featured.errorText", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Mensaje de error",
        maxLength: 300,
      }),
      field("catalog.featured.ctaLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto del botón",
        maxLength: 100,
      }),
      field("catalog.cards.gap", SEGMENT_FIELD_TYPES.NUMBER, { label: "Separación entre cards", limits: { min: 0, max: 64, step: 1 }, uiGroup: "design" }),
      field("catalog.cards.radius", SEGMENT_FIELD_TYPES.NUMBER, { label: "Redondez de las cards", limits: { min: 0, max: 48, step: 1 }, uiGroup: "design" }),
      field("catalog.cards.backgroundColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Fondo de las cards", maxLength: 100, colorScope: "section" }),
      field("catalog.cards.backgroundOpacity", SEGMENT_FIELD_TYPES.NUMBER, { label: "Transparencia del fondo", limits: { min: 0, max: 1, step: 0.05 }, uiGroup: "design" }),
      field("catalog.cards.backgroundImageUrl", SEGMENT_FIELD_TYPES.IMAGE, {
        label: "Imagen de fondo de las cards",
        maxLength: URL_MAX_LENGTH,
        positionPath: "catalog.cards.backgroundImagePosition",
      }),
      field("catalog.cards.backgroundImagePosition", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Posición del fondo de las cards",
        options: IMAGE_POSITION_OPTIONS,
      }),
      field("catalog.cards.backgroundImageFit", SEGMENT_FIELD_TYPES.SELECT, {
        label: "Ajuste del fondo de las cards",
        options: [
          { value: "cover", label: "Cubrir la card" },
          { value: "contain", label: "Mostrar completa" },
        ],
        uiGroup: "design",
      }),
      field("catalog.cards.backgroundImageOpacity", SEGMENT_FIELD_TYPES.NUMBER, { label: "Opacidad de la imagen de fondo", limits: { min: 0, max: 1, step: 0.05 }, uiGroup: "design" }),
      field("catalog.cards.imagePosition", SEGMENT_FIELD_TYPES.SELECT, { label: "Posición de la foto del ítem", options: [
        { value: "top", label: "Arriba" }, { value: "left", label: "Izquierda" }, { value: "right", label: "Derecha" },
      ], uiGroup: "design" }),
      field("catalog.cards.imageFit", SEGMENT_FIELD_TYPES.SELECT, { label: "Comportamiento de la foto del ítem", options: [
        { value: "cover", label: "Cubrir" }, { value: "contain", label: "Contener completa" },
      ], uiGroup: "design" }),
      field("catalog.cards.imageHeight", SEGMENT_FIELD_TYPES.NUMBER, { label: "Alto de imagen", limits: { min: 90, max: 420, step: 5 }, uiGroup: "design" }),
      field("catalog.cards.showImage", SEGMENT_FIELD_TYPES.BOOLEAN, { label: "Mostrar imagen del ítem" }),
      field("catalog.cards.showTitle", SEGMENT_FIELD_TYPES.BOOLEAN, { label: "Mostrar nombre / título" }),
      field("catalog.cards.showButton", SEGMENT_FIELD_TYPES.BOOLEAN, { label: "Mostrar botón principal" }),
      field("catalog.cards.showType", SEGMENT_FIELD_TYPES.BOOLEAN, { label: "Mostrar tipo de ítem" }),
      field("catalog.cards.showCategory", SEGMENT_FIELD_TYPES.BOOLEAN, { label: "Mostrar categoría" }),
      field("catalog.cards.showSubcategory", SEGMENT_FIELD_TYPES.BOOLEAN, { label: "Mostrar subcategoría" }),
      field("catalog.cards.showDescription", SEGMENT_FIELD_TYPES.BOOLEAN, { label: "Mostrar descripción" }),
      field("catalog.cards.showPrice", SEGMENT_FIELD_TYPES.BOOLEAN, { label: "Mostrar precio" }),
      field("catalog.cards.showDuration", SEGMENT_FIELD_TYPES.BOOLEAN, { label: "Mostrar duración en servicios" }),
      field("catalog.cards.showCapabilities", SEGMENT_FIELD_TYPES.BOOLEAN, { label: "Mostrar reserva / entrega" }),
      field("catalog.cards.showQuickCart", SEGMENT_FIELD_TYPES.BOOLEAN, { label: "Mostrar compra rápida" }),
      field("catalog.cards.borderColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Borde de la card", maxLength: 100, colorScope: "section" }),
      field("catalog.cards.shadowColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Sombra de la card", maxLength: 100, colorScope: "section" }),
      field("catalog.cards.shadowOpacity", SEGMENT_FIELD_TYPES.NUMBER, { label: "Opacidad de la sombra", defaultValue: 1, limits: { min: 0, max: 1, step: 0.05 }, uiGroup: "design" }),
      field("catalog.cards.shadowX", SEGMENT_FIELD_TYPES.NUMBER, { label: "Sombra · desplazamiento X", defaultValue: 0, limits: { min: -100, max: 100, step: 1 }, uiGroup: "design" }),
      field("catalog.cards.shadowY", SEGMENT_FIELD_TYPES.NUMBER, { label: "Sombra · desplazamiento Y", defaultValue: 8, limits: { min: -100, max: 100, step: 1 }, uiGroup: "design" }),
      field("catalog.cards.shadowBlur", SEGMENT_FIELD_TYPES.NUMBER, { label: "Sombra · desenfoque", defaultValue: 24, limits: { min: 0, max: 160, step: 1 }, uiGroup: "design" }),
      field("catalog.cards.shadowSpread", SEGMENT_FIELD_TYPES.NUMBER, { label: "Sombra · expansión", defaultValue: 0, limits: { min: -80, max: 80, step: 1 }, uiGroup: "design" }),
      field("catalog.cards.typeColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Color del tipo", maxLength: 100, colorScope: "section" }),
      field("catalog.cards.titleColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Color del título", maxLength: 100, colorScope: "section" }),
      field("catalog.cards.categoryColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Color de categoría", maxLength: 100, colorScope: "section" }),
      field("catalog.cards.subcategoryColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Color de subcategoría", maxLength: 100, colorScope: "section" }),
      field("catalog.cards.descriptionColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Color de descripción", maxLength: 100, colorScope: "section" }),
      field("catalog.cards.priceColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Color del precio", maxLength: 100, colorScope: "section" }),
      field("catalog.cards.buttonTextColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Texto del botón", maxLength: 100, colorScope: "section" }),
      field("catalog.cards.buttonBackgroundColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Fondo del botón", maxLength: 100, colorScope: "section" }),
      field("catalog.cards.buttonHoverTextColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Texto del botón al pasar el mouse", maxLength: 100, colorScope: "section" }),
      field("catalog.cards.buttonHoverBackgroundColor", SEGMENT_FIELD_TYPES.COLOR, { label: "Fondo del botón al pasar el mouse", maxLength: 100, colorScope: "section" }),
      field("catalog.cards.ctaLabel", SEGMENT_FIELD_TYPES.TEXT, { label: "Botón para productos", maxLength: 100 }),
      field("catalog.cards.serviceCtaLabel", SEGMENT_FIELD_TYPES.TEXT, { label: "Botón para servicios", maxLength: 100 }),
      field("catalog.cards.ctaBehavior", SEGMENT_FIELD_TYPES.SELECT, { label: "Destino del botón", options: [
        { value: "detail", label: "Detalle / reserva del ítem" }, { value: "custom", label: "Link personalizado" },
      ], uiGroup: "behavior" }),
      field("catalog.cards.ctaUrl", SEGMENT_FIELD_TYPES.URL, { label: "Link personalizado de las cards", maxLength: URL_MAX_LENGTH }),
      ...globalColorProperties(),
      visualStyleProperty(SEGMENT_TYPES.FEATURED),
    ],
  }),
  [SEGMENT_TYPES.BRANDS]: commonSegment({
    id: SEGMENT_TYPES.BRANDS,
    visibilityPath: "sections.showBrands",
    requiresProducts: true,
    elementEditingReady: true,
    elementTargets: {
      "catalog.brands.ctaLabel": {
        kind: "button",
        label: "Acción de marca",
        labelPath: "catalog.brands.ctaLabel",
        chips: ["Abre catálogo"],
      },
      "@brands.items": {
        kind: "collection-manager",
        label: "Marcas",
        collectionPath: "catalog.brands.items",
        editorMode: "brands",
        chips: ["Hasta 40"],
      },
    },
    shellAdvancedGroups: [
      {
        label: "Mensajes y estados",
        paths: [
          "catalog.brands.loadingText",
          "catalog.brands.emptyText",
          "catalog.brands.errorText",
        ],
      },
    ],
    properties: [
      field("sections.showBrands", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar marcas",
      }),
      field("catalog.brands.eyebrow", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto superior",
        maxLength: 180,
      }),
      field("catalog.brands.title", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Título",
        maxLength: 220,
      }),
      field("catalog.brands.subtitle", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Descripción",
        maxLength: 900,
      }),
      field("catalog.brands.loadingText", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Mensaje de carga",
        maxLength: 220,
      }),
      field("catalog.brands.emptyText", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Mensaje sin marcas",
        maxLength: 300,
      }),
      field("catalog.brands.errorText", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Mensaje de error",
        maxLength: 300,
      }),
      field("catalog.brands.ctaLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto del botón",
        maxLength: 100,
      }),
      field("catalog.brands.items", SEGMENT_FIELD_TYPES.COLLECTION, {
        label: "Marcas",
        limits: { maxItems: 40 },
        item: {
          fields: [
            { key: "name", type: SEGMENT_FIELD_TYPES.TEXT, maxLength: 160 },
            { key: "logoUrl", type: SEGMENT_FIELD_TYPES.IMAGE, maxLength: URL_MAX_LENGTH },
            { key: "alt", type: SEGMENT_FIELD_TYPES.TEXT, maxLength: 220 },
            { key: "query", type: SEGMENT_FIELD_TYPES.TEXT, maxLength: 180 },
          ],
        },
      }),
      ...globalColorProperties(),
      visualStyleProperty(SEGMENT_TYPES.BRANDS),
    ],
  }),
  [SEGMENT_TYPES.CATEGORIES]: commonSegment({
    id: SEGMENT_TYPES.CATEGORIES,
    visibilityPath: "sections.showCategories",
    requiresProducts: true,
    elementEditingReady: true,
    elementTargets: {
      "catalog.categories.ctaLabel": {
        kind: "button",
        label: "Acción de categoría",
        labelPath: "catalog.categories.ctaLabel",
        chips: ["Abre categoría"],
      },
      "@categories.generated": {
        kind: "catalog-derived",
        label: "Categorías automáticas",
        chips: ["Desde tu catálogo"],
      },
      "@categories.item-naming": {
        kind: "catalog-item-naming",
        label: "Cómo llamamos a tus ítems",
        singularPath: "catalog.categories.productSingular",
        pluralPath: "catalog.categories.productPlural",
        chips: ["1 / varios"],
      },
    },
    shellAdvancedGroups: [
      {
        label: "Mensajes y estados",
        paths: [
          "catalog.categories.loadingText",
          "catalog.categories.emptyText",
          "catalog.categories.errorText",
        ],
      },
    ],
    properties: [
      field("sections.showCategories", SEGMENT_FIELD_TYPES.BOOLEAN, {
        label: "Mostrar categorías",
      }),
      field("catalog.categories.eyebrow", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto superior",
        maxLength: 180,
      }),
      field("catalog.categories.title", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Título",
        maxLength: 220,
      }),
      field("catalog.categories.subtitle", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Descripción",
        maxLength: 900,
      }),
      field("catalog.categories.loadingText", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Mensaje de carga",
        maxLength: 220,
      }),
      field("catalog.categories.emptyText", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Mensaje sin categorías",
        maxLength: 300,
      }),
      field("catalog.categories.errorText", SEGMENT_FIELD_TYPES.TEXTAREA, {
        label: "Mensaje de error",
        maxLength: 300,
      }),
      field("catalog.categories.ctaLabel", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Texto del botón",
        maxLength: 100,
      }),
      field("catalog.categories.productSingular", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Nombre singular del producto",
        maxLength: 60,
      }),
      field("catalog.categories.productPlural", SEGMENT_FIELD_TYPES.TEXT, {
        label: "Nombre plural del producto",
        maxLength: 60,
      }),
      ...globalColorProperties(),
      visualStyleProperty(SEGMENT_TYPES.CATEGORIES),
    ],
  }),
};

export default commerceSegments;
